import {type AutocompleteState, createAutocomplete} from '@algolia/autocomplete-core'
import {announce} from '@react-aria/live-announcer'
import type {Dispatch} from 'react'

import {type Airport2, getAirports} from '@/lib/flight/flightService'
import debouncePromise from '@/utils/debouncePromise'

import style from './InnosearchAirportSelector.module.scss'

// Debounce the airport search
const debounced = debouncePromise(async items => Promise.resolve(items), 100) as <
  T extends unknown[],
>(
  items: T,
) => Promise<T>

interface Props {
  icon?: ReactNode
  label: string
  placeholder?: string
  inputValue: string
  setInputValue: Dispatch<string>
  selectedAirport: string | null
  setSelectedAirport: Dispatch<string | null>
  errorMessage?: string
}

interface AirportItem extends Record<string, unknown>, Airport2 {}

const InnosearchAirportSelector = deepMemo<Props>(function InnosearchAirportSelector({
  icon,
  label,
  placeholder,
  inputValue,
  setInputValue,
  selectedAirport,
  setSelectedAirport,
  errorMessage,
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const rejectNextInputValueChange = useRef(false)

  const [autocompleteState, setAutocompleteState] = useState<AutocompleteState<AirportItem>>({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: inputValue,
    activeItemId: null,
    status: 'idle',
  })
  const latestSelectedAirport = useLatest(selectedAirport)

  const autocomplete = useMemo(
    () =>
      createAutocomplete<AirportItem, SyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
        // debug: true, // Keep it open when development
        placeholder,
        initialState: {
          query: latestSelectedAirport.current ?? '',
        },
        openOnFocus: true,
        onStateChange(params) {
          setAutocompleteState(params.state)
        },
        async getSources() {
          return debounced([
            {
              sourceId: 'airports',
              getItemInputValue(params) {
                return params.item.airport_name
              },
              async getItems(params) {
                if (!params.query) return []
                try {
                  return (await getAirports({q: params.query})) as AirportItem[]
                } catch {
                  return []
                }
              },
              onSelect(params) {
                inputRef.current?.blur()
                params.setIsOpen(false)
                setSelectedAirport(params.item.airport_code)
              },
              onResolve: params => {
                if (!Array.isArray(params.items)) {
                  return
                }

                const totalAirports = params.items.reduce(
                  (total: number, item) => total + (Array.isArray(item) ? item.length : 1),
                  0,
                )

                announce(`Found ${totalAirports} airports for "${params.state.query}"`, 'polite')
              },
            },
          ])
        },
      }),
    [placeholder, setSelectedAirport],
  )

  useEffect(() => {
    if (rejectNextInputValueChange.current) {
      rejectNextInputValueChange.current = false
      return
    }
    setSelectedAirport(null)
    setInputValue(autocompleteState.query)
  }, [autocompleteState.query, setSelectedAirport, setInputValue])

  useEffect(
    function loadDefaultSelectedAirport() {
      void (async () => {
        if (!latestSelectedAirport.current) return

        try {
          const airport = (await getAirports({q: latestSelectedAirport.current}))[0]
          if (!airport) throw new Error('Airport not found')
          rejectNextInputValueChange.current = true
          autocomplete.setQuery(airport.airport_name)
        } catch (e) {
          console.error(e)
          // do nothing
        }
      })()
    },
    [setInputValue, autocomplete],
  )

  const {getEnvironmentProps} = autocomplete

  useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) return undefined

    // TODO: check this error
    // eslint-disable-next-line @typescript-eslint/unbound-method -- dont know how to fix it
    const {onTouchStart, onTouchMove, onMouseDown} = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    })

    const abortController = new AbortController()

    window.addEventListener('mousedown', onMouseDown, {signal: abortController.signal})
    window.addEventListener('touchstart', onTouchStart, {signal: abortController.signal})
    window.addEventListener('touchmove', onTouchMove, {signal: abortController.signal})

    return () => {
      abortController.abort()
    }
  }, [getEnvironmentProps, autocompleteState.isOpen])

  return (
    <div className={style.Container} {...autocomplete.getRootProps({role: 'none'})}>
      <form
        ref={formRef}
        className={style.Form}
        {...autocomplete.getFormProps({inputElement: inputRef.current})}
      >
        <div className={style.InputGroup}>
          {icon}
          <input
            ref={inputRef}
            name={label}
            className={clsx(style.Input)}
            {...autocomplete.getInputProps({inputElement: inputRef.current})}
          />
        </div>
      </form>

      {errorMessage && <div className={style.ErrorMessage}>{errorMessage}</div>}

      {autocompleteState.isOpen && (
        <div ref={panelRef} className={style.Panel} {...autocomplete.getPanelProps({})}>
          {autocompleteState.collections.map((collection, index) => {
            const {items} = collection

            return (
              <section key={`source-${index}`}>
                {items.length > 0 && (
                  <ul className={style.List} {...autocomplete.getListProps()}>
                    {items.map((item, index) => (
                      <li
                        key={item.airport_code}
                        className={clsx(
                          style.Item,
                          autocompleteState.activeItemId === index && style.Active,
                        )}
                        {...autocomplete.getItemProps({
                          item,
                          source: collection.source,
                        })}
                      >
                        <div className={style.AirportName}>{item.airport_name}</div>
                        <div className={style.AirportCode}>{item.airport_code}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
})(['icon'])

export default InnosearchAirportSelector
